import { debounce } from 'lodash'
import { UrlAudits } from 'handsontable/utils/codes.url-audits'
import {
  isArraySequential,
  getCellValuesFromRange,
  getNumbersFromString,
  updateChangesWithIncrementedValues,
} from '../utils/beforeChanges'

interface CellMetadata {
  row: number | null
  validationErrors?: string[]
  comment?: string
  link_governance?: any
  linkField?: any
}

interface Cell {
  cell_value: string | null
  metadata: CellMetadata
}

type Row = Record<string, Cell>

interface Column {
  governance_type: string
  field: {
    governance_type: string
    link_governance?: any
  }
  template_id?: string
}

interface DataContainer {
  data: Row[]
  allRenderColumns: Record<string, Column>
}

interface HotTable {
  getSettings: () => {
    binder?: any
    columns: any[]
  }
  getSelected: () => number[][]
  render: () => void
}

function beforeChangeBinderCallbacks(hotTable, changes) {
  const { binder } = hotTable.getSettings()

  if (!binder) return

  changes.forEach(change => {
    binder.recordChange(change, { batch: true })
  })

  // Manually call the changeCallback when using batch with recordChange; much faster.
  binder.dataContainer.changeCallback()
}

/**
 * Trigger a record change to re-evalute cells in the same row.
 * - e.g. A row might have required fields, remove the only remaining cell value and any other required fields cancel out their validation errors.
 * - beforeChangeBinderCallbacks is called at the end of every potential change, instead of returning,
 * with the exception of a source of 'CopyPaste.paste'.
 * @param {*} changes Cell data
 * @param {*} source
 */
export function beforeChangeUpdateCellTree(changes, source, hotTable) {
  if (source === 'CopyPaste.paste') return

  // TODO: Add incrementing support across multiple columns; currently supporting 1 column.
  /**
   * Autofill.fill source is used to determine if the user is dragging cells to fill.
   * - If the user is dragging cells to fill, detect incremental values and continue the sequence.
   */
  if (source === 'Autofill.fill') {
    // TODO: Disable Autofill.fill on incremented numbers when a user holds down shift; this allows a user to opt-out of incrementing.
    // We need to be able to detct if the user is holding down shift, and the only straight-forward way to do this is using the
    // ShortcutManager from Handsontable v14+. We're not currently on this version (12.4.0). The new version provides a way to listen
    // for key events.
    // const shortcutManager = hotTable.getShortcutManager()

    const singleSelection = hotTable.getSelected()
    const [startRow, startCol, endRow] = singleSelection[0]
    const isDateColumn = hotTable.getSettings().columns[startCol].type === 'date'

    // Do not increment if the column is a date column.
    if (isDateColumn) {
      beforeChangeBinderCallbacks(hotTable, changes)
      return
    }

    // Only incremement over a multi-row selection.
    if (startRow === endRow) {
      beforeChangeBinderCallbacks(hotTable, changes)
      return
    }

    const cellValuesFromRange = getCellValuesFromRange(startRow, endRow, startCol, hotTable)

    const result: number[][] = []

    // Parse all the numbers out of the strings from the cell values
    // and store them in a 2D array so that we can check if they are sequential.
    // We do this to determine if the user is incrementing a sequence of numbers.
    // - The cell value may contain multiple numbers.
    // e.g. ['foo1', 'foo2', 'foo3'] => [[1, 2, 3]]
    // e.g. ['Campaign 1001', 'Campaign 1002', 'Campaign 1003'] => [[1001, 1002, 1003]]
    for (const cellValue of cellValuesFromRange) {
      const numbers = getNumbersFromString(cellValue) // get numbers to check if they are sequential

      for (const [j, number] of numbers.entries()) {
        if (!result[j]) result[j] = []
        result[j].push(number)
      }
    }

    // Loop through the 2d array of numbers to check if each group is sequential.
    // A sequential group of numbers means the user is incrementing a sequence of numbers,
    // so we can continue the sequence.
    for (const sequence of result) {
      const { isSequential } = isArraySequential(sequence)

      if (!isSequential) {
        break
      }
    }

    changes = updateChangesWithIncrementedValues({
      cellValues: cellValuesFromRange,
      changes,
    })
  }

  // todo: we can probably be smarter here.  If we are editing multiple cells, and some
  // are children of the others, we don't need to trigger an update on those.

  beforeChangeBinderCallbacks(hotTable, changes)
}

/**
 * Skip validating a row when it's empty.
 * 1. Determined by the missing index position of the metadata row value.
 * @param {object} params
 * @param {object} params.row The data row.
 * @returns {boolean}
 */
function skipEmptyRowValidation({ row }: { row: Row }) {
  const fieldsInRow = Object.values(row)
  const canSkip = !fieldsInRow.some(field => field?.metadata?.row !== null)

  return canSkip
}

/**
 * Updates the invalid cell state on a debounced timer in case of extra renders.
 *
 * @type {Function|(() => void)}
 */
export const updateInvalidCellState = debounce(
  (
    component: any,
    dataContainer: DataContainer,
    onInvalidCellsUpdated: (cells: Cell[]) => void,
  ) => {
    const { data } = dataContainer
    let invalidCells: Cell[] = []
    data.forEach((row, index) => {
      if (skipEmptyRowValidation({ row })) {
        return
      }

      invalidCells = invalidCells.concat(
        Object.values(row)
          .filter(
            cell =>
              (cell?.metadata?.validationErrors?.length ?? 0) > 0 ||
              Boolean(cell?.metadata?.comment),
          )
          .map(cell => ({
            ...cell,
            metadata: {
              ...cell.metadata,
              row: index,
            },
          })),
      )
    })

    if (component) {
      component.validationErrors = invalidCells
    }

    onInvalidCellsUpdated(invalidCells)
  },
  100,
)

export function onTableClick(
  event: MouseEvent,
  handsOnTable: HotTable,
  dataContainer: DataContainer,
) {
  const target = event.target as HTMLElement
  // process link refresh clicks
  if (
    typeof target?.className === 'string' && // this check is for svg items that don't have a string classname IE: datepicker
    target.className.includes('link-refresh')
  ) {
    const urlToValidate = target.getAttribute('data-url')
    const fieldId = target.getAttribute('data-field-id')
    const rowNum = Number(target.getAttribute('data-row-num'))
    UrlAudits.removeUrl(urlToValidate)

    const templateId = Object.values(dataContainer.allRenderColumns).find(
      tf => tf.template_id,
    )?.template_id

    UrlAudits.getLinkStatusAndRefresh({
      dataContainer,
      templateId,
      url:
        typeof fieldId === 'string'
          ? (dataContainer.data[rowNum]?.[fieldId]?.cell_value ?? '')
          : '',
      preventCache: true,
    })

    handsOnTable?.render()
  }
}
